import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePageSize from "@/hooks/usePageSize";
import usePopupStore from "@/hooks/usePopupStore";
import useSearchFilters from "@/hooks/useSearchFilters";
import ActionsBar from "@/UI/components/ActionsBar";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import FilterRange from "@/UI/inputs/FilterRange";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import BranchesForm from "@/UI/forms/BranchesForm";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import MapViewPopup from "@/UI/popups/MapViewPopup";
import SidePopup from "@/UI/popups/SidePopup";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

interface GeoJSONLocation {
    type?: string;
    coordinates?: [number, number];
}

interface Branch {
    _id: string;
    name: string;
    address: string;
    location?: { lat: number; lng: number } | GeoJSONLocation | null;
}

function parseLocation(loc: Branch["location"]): { lat: number; lng: number } | null {
    if (!loc) return null;
    if ("lat" in loc && "lng" in loc) return { lat: loc.lat, lng: loc.lng };
    if ("coordinates" in loc && Array.isArray(loc.coordinates) && loc.coordinates.length >= 2) {
        const [lng, lat] = loc.coordinates;
        if (lng !== 0 || lat !== 0) return { lat, lng };
    }
    return null;
}

export default function BranchesScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const { pageSize, setPageSize } = usePageSize();

    const filters = useSearchFilters({
        search: undefined as string | undefined,
        min_rate: undefined as string | undefined,
        max_rate: undefined as string | undefined,
    });

    // ── Branches fetch ─────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("branches", { query: { page: 1, limit: pageSize, ...filters.committedQuery } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: pageSize, ...filters.committedQuery } });
    }, [page]);

    useEffect(() => {
        const handler = () => {
            setPage(1);
            invoke({ query: { page: 1, limit: pageSize, ...filters.committedQuery } });
        };
        window.addEventListener("branches:refresh", handler);
        return () => window.removeEventListener("branches:refresh", handler);
    }, []);

    const branches: Branch[] = data?.data?.branches ?? [];
    const total: number = data?.data?.total ?? 0;

    const handlePageSizeChange = (size: number) => {
        setPage(1);
        setPageSize(size);
        invoke({ query: { page: 1, limit: size, ...filters.committedQuery } });
    };

    // ── Delete fetch ───────────────────────────────────────────────────────────
    const { loading: deleting, status: deleteStatus, invoke: deleteInvoke } = useFetch("branches", { method: "DELETE" }, false);

    const wasDeleting = useRef(false);
    useEffect(() => {
        if (deleting) {
            wasDeleting.current = true;
        } else if (wasDeleting.current) {
            wasDeleting.current = false;
            setDeletingId(null);
            if (deleteStatus === 200) invoke({ query: { page, limit: pageSize, ...filters.committedQuery } });
        }
    }, [deleting]);

    const handleDelete = (id: string) => {
        if (deleting) return;
        setDeletingId(id);
        deleteInvoke({ param: id });
    };

    const openDeleteConfirm = (branch: Branch) => {
        const name = "confirm-delete-branch";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: t.branches.deleteConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => handleDelete(branch._id),
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Get-one fetch (edit / duplicate) ───────────────────────────────────────
    const pendingModeRef = useRef<"edit" | "duplicate" | null>(null);
    const { loading: fetchingOne, data: oneData, invoke: fetchOne } = useFetch("branches", { method: "GET" }, false);

    const openBranchForm = (mode: "edit" | "duplicate", branch: any) => {
        const name = mode === "edit" ? "edit-branch" : "duplicate-branch";
        const title = mode === "edit" ? t.branchForm.editTitle : t.branchForm.duplicateTitle;
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title,
                Content: BranchesForm,
                contentDetails: {
                    branchId: mode === "edit" ? branch._id : undefined,
                    initialData: branch,
                    isDuplicate: mode === "duplicate",
                    onSuccess: () => {
                        invoke({ query: { page: 1, limit: pageSize, ...filters.committedQuery } });
                        setPage(1);
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    const wasFetchingOne = useRef(false);
    useEffect(() => {
        if (fetchingOne) {
            wasFetchingOne.current = true;
        } else if (wasFetchingOne.current) {
            wasFetchingOne.current = false;
            const branch = oneData?.data?.branch;
            if (branch && pendingModeRef.current) {
                openBranchForm(pendingModeRef.current, branch);
                pendingModeRef.current = null;
            }
        }
    }, [fetchingOne]);

    const handleEdit = (row: Branch) => {
        pendingModeRef.current = "edit";
        fetchOne({ param: row._id });
    };

    const handleDuplicate = (row: Branch) => {
        pendingModeRef.current = "duplicate";
        fetchOne({ param: row._id });
    };

    // ── Add Branch ─────────────────────────────────────────────────────────────
    const openAddBranchForm = () => {
        const name = "add-branch";
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title: t.branchForm.createTitle,
                Content: BranchesForm,
                contentDetails: {
                    onSuccess: () => {
                        window.dispatchEvent(new CustomEvent("branches:refresh"));
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── View Location on Map ──────────────────────────────────────────────────
    const openLocationMap = (branch: Branch) => {
        const loc = parseLocation(branch.location);
        if (!loc) return;
        const name = "view-branch-location";
        removeComp(name);
        addComp({
            name,
            Component: MapViewPopup,
            details: { title: branch.name, lat: loc.lat, lng: loc.lng },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Columns ────────────────────────────────────────────────────────────────
    const columns: TableColumn<Branch>[] = [
        {
            key: "name",
            header: t.branches.col.name,
            width: "200px",
            render: (row) => <TableCell.Text value={row.name || "—"} maxChars={30} />,
        },
        {
            key: "address",
            header: t.branches.col.address,
            width: "250px",
            render: (row) => <TableCell.Text value={row.address || "—"} maxChars={40} />,
        },
        {
            key: "location",
            header: t.branches.col.location,
            width: "100px",
            render: (row) => {
                const loc = parseLocation(row.location);
                if (loc) {
                    return (
                        <button type="button" className="branches-location-btn" onClick={() => openLocationMap(row)}>
                            <img src="/icons/location-icon.svg" alt="" width={14} height={14} />
                            <span>{t.branches.hasLocation}</span>
                        </button>
                    );
                }
                return <TableCell.Badge label={t.branches.noLocation} variant="gray" />;
            },
        },
        {
            key: "actions",
            header: t.branches.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions
                    disabled={deletingId === row._id || fetchingOne}
                    actions={[
                        { name: "edit", icon: "/icons/edit-icon.svg", handler: () => handleEdit(row) },
                        { name: "duplicate", icon: "/icons/duplicate-icon.svg", handler: () => handleDuplicate(row) },
                        { name: "delete", icon: "/icons/delete-icon.svg", handler: () => openDeleteConfirm(row), danger: true },
                    ]}
                />
            ),
        },
    ];

    const filterContent = (
        <>
            <FilterSearchInput
                value={filters.draft.search}
                onChange={(v) => filters.setDraftField("search", v)}
                placeholder={t.filters.branches.search}
            />
            <FilterRange
                label={t.filters.branches.rateRange}
                fromValue={filters.draft.min_rate}
                toValue={filters.draft.max_rate}
                onFromChange={(v) => filters.setDraftField("min_rate", v)}
                onToChange={(v) => filters.setDraftField("max_rate", v)}
                fromPlaceholder={t.filters.from}
                toPlaceholder={t.filters.to}
            />
        </>
    );

    return (
        <div className="branches-screen">
            <ActionsBar
                onAdd={openAddBranchForm}
                filterContent={filterContent}
                filterOpen={filters.filterOpen}
                onFilterOpen={filters.openFilter}
                onFilterSave={() =>
                    filters.saveFilter((q) => {
                        setPage(1);
                        invoke({ query: { page: 1, limit: pageSize, ...q } });
                    })
                }
                onFilterClear={() =>
                    filters.clearFilter(() => {
                        setPage(1);
                        invoke({ query: { page: 1, limit: pageSize } });
                    })
                }
                onFilterCancel={filters.cancelFilter}
                hasActiveFilters={filters.hasActiveFilters}
            />
            <div className="branches-screen-content">
                <Table
                    columns={columns}
                    data={branches}
                    loading={loading}
                    rowKey="_id"
                    pagination={{ page, pageSize, total, onPageChange: setPage, onPageSizeChange: handlePageSizeChange }}
                />
            </div>
        </div>
    );
}
